/**
 * CreateDatabaseDialog Component
 *
 * Dialog for creating a new database on a MongoDB server.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { serverApi, MongoServer, CreateDatabaseInput } from '@/services/api/serverApi';

interface CreateDatabaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server: MongoServer | null;
  onSuccess: () => void;
}

export const CreateDatabaseDialog: React.FC<CreateDatabaseDialogProps> = ({
  open,
  onOpenChange,
  server,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateDatabaseInput>({
    databaseName: '',
    description: '',
    isDefault: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof CreateDatabaseInput, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!server) {
      setError('No server selected');
      return;
    }

    const dbName = formData.databaseName.trim().toLowerCase();

    if (!dbName) {
      setError('Database name is required');
      return;
    }

    // Validate name format
    if (!/^[a-z][a-z0-9_-]*$/.test(dbName)) {
      setError('Database name must start with a letter and contain only lowercase letters, numbers, underscores, and dashes');
      return;
    }

    // Check for reserved names
    if (['admin', 'config', 'local'].includes(dbName)) {
      setError(`"${dbName}" is a reserved database name`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await serverApi.createDatabase(server.id, {
        ...formData,
        databaseName: dbName,
      });
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err: unknown) {
      // Handle both Error objects and API error objects
      let errorMessage = 'Failed to create database';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = String((err as { message: unknown }).message);
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      databaseName: '',
      description: '',
      isDefault: false,
    });
    setError(null);
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Database</DialogTitle>
          {server && (
            <DialogDescription>
              Create a new database on <strong>{server.name}</strong> ({server.host}:{server.port})
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Database Name */}
          <div className="space-y-2">
            <Label htmlFor="databaseName">Database Name</Label>
            <Input
              id="databaseName"
              value={formData.databaseName}
              onChange={(e) => handleChange('databaseName', e.target.value.toLowerCase())}
              placeholder="e.g., sensor-data, analytics"
              required
            />
            <p className="text-xs text-muted-foreground">
              Use lowercase letters, numbers, underscores, and dashes. Must start with a letter.
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="e.g., Stores sensor readings from IoT devices"
            />
          </div>

          {/* Set as Default */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isDefault"
              checked={formData.isDefault}
              onCheckedChange={(checked) => handleChange('isDefault', checked === true)}
            />
            <Label htmlFor="isDefault" className="text-sm font-normal cursor-pointer">
              Set as default database
            </Label>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.databaseName.trim()}
            >
              {isSubmitting ? 'Creating...' : 'Create Database'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDatabaseDialog;
